import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWD307Query } from './hooks/usePWD307Query';
import type { PurchaseRow, UnitSummary } from '../../services/pwd307Api';
import './PWD307Form.css';

const PWD307Form: React.FC = () => {
  const { loading, data, unitSummary, queried, noData, runQuery, reset } = usePWD307Query();

  const currentYear = new Date().getFullYear();
  const [curYearStart, setCurYearStart] = useState(`${currentYear}01`);
  const [curYearEnd, setCurYearEnd] = useState(`${currentYear}12`);
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [itemFrom, setItemFrom] = useState('');
  const [itemTo, setItemTo] = useState('');

  const buildCmpYear = (ym: string) => {
    if (!ym || ym.length < 6) return '';
    const y = parseInt(ym.substring(0, 4), 10) - 1;
    return `${y}${ym.substring(4)}`;
  };

  const handleQuery = () => {
    runQuery({
      PR_START_01: curYearStart.trim() || undefined,
      PR_LAST_01: curYearEnd.trim() || undefined,
      PR_START_04: buildCmpYear(curYearStart) || undefined,
      PR_LAST_04: buildCmpYear(curYearEnd) || undefined,
      PR_START_02: vendorFrom.trim() || undefined,
      PR_LAST_02: vendorTo.trim() || undefined,
      PR_START_03: itemFrom.trim() || undefined,
      PR_LAST_03: itemTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setCurYearStart(`${currentYear}01`);
    setCurYearEnd(`${currentYear}12`);
    setVendorFrom('');
    setVendorTo('');
    setItemFrom('');
    setItemTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>廠商年度採購數量</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>廠商年度採購數量</h2>
<table><thead><tr><th>廠商代號</th><th>廠商名稱</th><th>品項代號</th><th>單位</th><th>當年數量</th><th>當年金額</th><th>比較年數量</th><th>比較年金額</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FD110_12}</td><td>${r.vendor_name}</td><td>${r.item_code}</td><td>${r.FD111_16}</td><td class="right">${r.cur_qty}</td><td class="right">${r.cur_amt.toFixed(2)}</td><td class="right">${r.cmp_qty}</td><td class="right">${r.cmp_amt.toFixed(2)}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<PurchaseRow> = [
    { title: '廠商代號', dataIndex: 'FD110_12', key: 'FD110_12', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true, width: 160 },
    { title: '品項代號', dataIndex: 'item_code', key: 'item_code', width: 130 },
    { title: '單位', dataIndex: 'FD111_16', key: 'FD111_16', width: 60 },
    { title: '當年數量', dataIndex: 'cur_qty', key: 'cur_qty', width: 90, align: 'right' as const },
    { title: '當年金額', dataIndex: 'cur_amt', key: 'cur_amt', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '比較年數量', dataIndex: 'cmp_qty', key: 'cmp_qty', width: 90, align: 'right' as const },
    { title: '比較年金額', dataIndex: 'cmp_amt', key: 'cmp_amt', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const summaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'FD111_16', key: 'FD111_16', width: 80 },
    { title: '當年合計數量', dataIndex: 'cur_qty', key: 'cur_qty', align: 'right' as const },
    { title: '當年合計金額', dataIndex: 'cur_amt', key: 'cur_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '比較年合計數量', dataIndex: 'cmp_qty', key: 'cmp_qty', align: 'right' as const },
    { title: '比較年合計金額', dataIndex: 'cmp_amt', key: 'cmp_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  return (
    <PermissionGuard module={Modules.PWD307} action="query">
      <div className="pwd307-container">
        <div className="pwd307-header">
          <h2>廠商年度採購數量</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>當年起 (YYYYMM)</label>
              <Input value={curYearStart} onChange={e => setCurYearStart(e.target.value)} placeholder="例 202501" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>當年迄 (YYYYMM)</label>
              <Input value={curYearEnd} onChange={e => setCurYearEnd(e.target.value)} placeholder="例 202512" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>廠商代號起</label>
              <Input value={vendorFrom} onChange={e => setVendorFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>廠商代號迄</label>
              <Input value={vendorTo} onChange={e => setVendorTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>品項代號起</label>
              <Input value={itemFrom} onChange={e => setItemFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>品項代號迄</label>
              <Input value={itemTo} onChange={e => setItemTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            <Table<PurchaseRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FD110_12}-${r.item_code}-${r.FD111_16}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 900 }}
              style={{ marginBottom: 16 }}
            />
            {unitSummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div>
                <Table<UnitSummary>
                  columns={summaryColumns}
                  dataSource={unitSummary}
                  rowKey="FD111_16"
                  size="small"
                  pagination={false}
                  scroll={{ x: 600 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWD307Form;
